import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Gift, Copy, Share2, Users, CheckCircle, Mail, 
  DollarSign, Link as LinkIcon, Send, Car, UserPlus
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * HostInviteAndEarn Component
 * 
 * Host dashboard section for referral program with two tabs:
 * - Invite Hosts: $100 reward when referred host completes first booking as host
 * - Invite Renters: $10 reward when referred user completes first booking as renter
 * 
 * This component reuses the existing referral backend system but adds
 * host-specific messaging and tracking.
 */
const HostInviteAndEarn = ({ api }) => {
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [stats, setStats] = useState({
    total_invites: 0,
    successful_referrals: 0,
    total_credits_earned: 0,
    current_balance: 0
  });
  const [hostStats, setHostStats] = useState({
    total_host_invites: 0,
    successful_host_referrals: 0,
    host_credits_earned: 0
  });
  const [inviteEmail, setInviteEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('hosts');

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      // Fetch referral code
      const codeResponse = await api.get('/users/referral-code');
      setReferralCode(codeResponse.data.referral_code);
      setReferralLink(codeResponse.data.referral_link);
      
      // Fetch stats
      const statsResponse = await api.get('/users/referral-stats');
      setStats(statsResponse.data);
      
      // Fetch host-specific stats
      try {
        const hostStatsResponse = await api.get('/users/host-referral-stats');
        setHostStats(hostStatsResponse.data);
      } catch (err) {
        // Host stats endpoint might not exist yet, use defaults
      }
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = (type = 'renter') => {
    const link = type === 'host' 
      ? `${referralLink}&type=host`
      : referralLink;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async (type = 'renter') => {
    const message = type === 'host'
      ? 'Join HireHub Australia as a host! List your vehicle and earn extra income. Use my link and we both get $100 credit!'
      : 'Sign up with my link and get $10 credit for your first booking!';
    
    const link = type === 'host' 
      ? `${referralLink}&type=host`
      : referralLink;

    if (navigator.share) {
      try {
        await navigator.share({
          title: type === 'host' ? 'Become a HireHub Host' : 'Join HireHub Australia',
          text: message,
          url: link
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          handleCopyLink(type);
        }
      }
    } else {
      handleCopyLink(type);
    }
  };

  const handleSendInvite = async (e, type = 'renter') => {
    e.preventDefault();
    
    if (!inviteEmail) {
      toast.error('Please enter an email address');
      return;
    }

    setSending(true);
    try {
      await api.post('/users/referral/send-invite', { 
        email: inviteEmail,
        invite_type: type // 'host' or 'renter'
      });
      toast.success(`${type === 'host' ? 'Host' : 'Renter'} invite sent successfully!`);
      setInviteEmail('');
      fetchReferralData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-slate-100">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-200 border-t-[#004B8D]"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-100 overflow-hidden hover:shadow-lg transition-shadow duration-150" data-testid="host-invite-and-earn-section">
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-[#004B8D] to-[#00A3BF] p-5 text-white">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Gift className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-lg">Invite & Earn</h3>
            <p className="text-white/80 text-sm">Earn credits by inviting hosts and renters</p>
          </div>
        </div>
      </div>
      
      <CardContent className="p-0">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full grid grid-cols-2 rounded-none border-b bg-transparent h-12">
            <TabsTrigger 
              value="hosts" 
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#004B8D] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              data-testid="invite-hosts-tab"
            >
              <Car className="w-4 h-4 mr-2" />
              Invite Hosts
            </TabsTrigger>
            <TabsTrigger 
              value="renters" 
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#004B8D] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              data-testid="invite-renters-tab"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Invite Renters
            </TabsTrigger>
          </TabsList>
          
          {/* Invite Hosts Tab */}
          <TabsContent value="hosts" className="p-5 space-y-5 m-0">
            {/* Host Referral Hero */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-bold text-amber-900 text-lg">Earn $100</p>
                  <p className="text-xs text-amber-700">When your referred host completes their first booking</p>
                </div>
              </div>
            </div>

            {/* Host Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-slate-50 rounded-xl p-3 text-center hover:bg-slate-100 transition-colors duration-150">
                <Car className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                <p className="text-xl font-bold text-slate-900">{hostStats.total_host_invites || 0}</p>
                <p className="text-xs text-slate-400">Hosts Invited</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-3 text-center hover:bg-slate-100 transition-colors duration-150">
                <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />
                <p className="text-xl font-bold text-slate-900">{hostStats.successful_host_referrals || 0}</p>
                <p className="text-xs text-slate-400">Completed</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-3 text-center hover:bg-slate-100 transition-colors duration-150">
                <DollarSign className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                <p className="text-xl font-bold text-slate-900">${hostStats.host_credits_earned || 0}</p>
                <p className="text-xs text-slate-400">Earned</p>
              </div>
            </div>

            {/* Host Referral Link */}
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Your Host Referral Link
              </Label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    value={`${referralLink}&type=host`}
                    readOnly
                    className="pl-10 pr-4 bg-slate-50 text-sm"
                    data-testid="host-referral-link-input"
                  />
                </div>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => handleCopyLink('host')}
                  className={copied ? 'bg-green-50 border-green-200' : ''}
                  data-testid="copy-host-link-btn"
                >
                  {copied ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => handleShare('host')}
                >
                  <Share2 className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Code: <span className="font-mono font-semibold">{referralCode}</span>
              </p>
            </div>

            {/* Send Host Invite by Email */}
            <div className="pt-4 border-t border-slate-100">
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Invite Host by Email
              </Label>
              <form onSubmit={(e) => handleSendInvite(e, 'host')} className="flex gap-2">
                <div className="flex-1 relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    type="email"
                    placeholder="future-host@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="pl-10"
                    data-testid="host-invite-email-input"
                  />
                </div>
                <Button 
                  type="submit"
                  disabled={sending}
                  className="bg-[#004B8D] hover:bg-[#003865]"
                  data-testid="send-host-invite-btn"
                >
                  {sending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* How Host Referral Works */}
            <div className="bg-amber-50 rounded-xl p-4">
              <h4 className="font-semibold text-slate-900 mb-2 text-sm">How Host Referrals Work</h4>
              <ol className="space-y-1.5 text-xs text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-amber-500 text-white rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold">1</span>
                  Share your host referral link
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-amber-500 text-white rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold">2</span>
                  They sign up and list their vehicle
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-amber-500 text-white rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold">3</span>
                  When they complete their first booking as host
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-amber-500 text-white rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold">4</span>
                  You get $100 credit!
                </li>
              </ol>
            </div>
          </TabsContent>

          {/* Invite Renters Tab */}
          <TabsContent value="renters" className="p-5 space-y-5 m-0">
            {/* Renter Referral Hero */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-bold text-emerald-900 text-lg">Earn $10</p>
                  <p className="text-xs text-emerald-700">When your referred friend completes their first booking</p>
                </div>
              </div>
            </div>

            {/* Renter Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-slate-50 rounded-xl p-3 text-center">
                <Users className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                <p className="text-xl font-bold text-slate-900">{stats.total_invites}</p>
                <p className="text-xs text-slate-500">Invites Sent</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-3 text-center">
                <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />
                <p className="text-xl font-bold text-slate-900">{stats.successful_referrals}</p>
                <p className="text-xs text-slate-500">Successful</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-3 text-center">
                <DollarSign className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                <p className="text-xl font-bold text-slate-900">${stats.total_credits_earned}</p>
                <p className="text-xs text-slate-500">Earned</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-3 text-center">
                <DollarSign className="w-5 h-5 text-purple-500 mx-auto mb-1" />
                <p className="text-xl font-bold text-slate-900">${stats.current_balance}</p>
                <p className="text-xs text-slate-500">Balance</p>
              </div>
            </div>

            {/* Renter Referral Link */}
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Your Referral Link
              </Label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    value={referralLink}
                    readOnly
                    className="pl-10 pr-4 bg-slate-50 text-sm"
                    data-testid="renter-referral-link-input"
                  />
                </div>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => handleCopyLink('renter')}
                  className={copied ? 'bg-green-50 border-green-200' : ''}
                  data-testid="copy-renter-link-btn"
                >
                  {copied ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => handleShare('renter')}
                >
                  <Share2 className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Code: <span className="font-mono font-semibold">{referralCode}</span>
              </p>
            </div>

            {/* Send Renter Invite by Email */}
            <div className="pt-4 border-t border-slate-100">
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Invite by Email
              </Label>
              <form onSubmit={(e) => handleSendInvite(e, 'renter')} className="flex gap-2">
                <div className="flex-1 relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    type="email"
                    placeholder="friend@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="pl-10"
                    data-testid="renter-invite-email-input"
                  />
                </div>
                <Button 
                  type="submit"
                  disabled={sending}
                  className="bg-[#004B8D] hover:bg-[#003865]"
                  data-testid="send-renter-invite-btn"
                >
                  {sending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* How Renter Referral Works */}
            <div className="bg-blue-50 rounded-xl p-4">
              <h4 className="font-semibold text-slate-900 mb-2 text-sm">How it works</h4>
              <ol className="space-y-1.5 text-xs text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-[#004B8D] text-white rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold">1</span>
                  Share your link with friends
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-[#004B8D] text-white rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold">2</span>
                  They sign up and complete their first booking
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-[#004B8D] text-white rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold">3</span>
                  You both get $10 credit!
                </li>
              </ol>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default HostInviteAndEarn;
